import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import LoginButton from '../components/auth/LoginButton';
import TasteProfileCard from '../components/preferences/TasteProfileCard';
import SwipeOnboarding from '../components/onboarding/SwipeOnboarding';
import TravelSoulQuiz from '../components/onboarding/TravelSoulQuiz';
import {
  UserCircleIcon,
  MapPinIcon,
  BookmarkIcon,
  ArrowRightOnRectangleIcon,
  EnvelopeIcon,
  CalendarIcon,
  ArrowPathIcon,
  SparklesIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

/* ── Layout ─────────────────────────────────────── */

const Container = styled.div`
  min-height: 100vh;
  background: #f5f6fa;
  padding: 80px 16px 100px;

  @media (min-width: 769px) {
    padding: 100px 24px 60px;
  }
`;

const Content = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

/* ── Login prompt (unauthenticated) ──────────────── */

const LoginPrompt = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 40px 24px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);

  @media (min-width: 769px) {
    padding: 56px 40px;
    margin-top: 20px;
  }
`;

const PromptIcon = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #4285F4 0%, #1A73E8 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 36px;
    height: 36px;
    color: white;
  }

  @media (min-width: 769px) {
    width: 84px;
    height: 84px;
    margin-bottom: 24px;

    svg {
      width: 42px;
      height: 42px;
    }
  }
`;

const PromptTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 10px;

  @media (min-width: 769px) {
    font-size: 26px;
    margin-bottom: 12px;
  }
`;

const PromptMessage = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0 0 28px;
  line-height: 1.55;

  @media (min-width: 769px) {
    font-size: 16px;
    margin-bottom: 32px;
  }
`;

/* ── Profile header card ─────────────────────────── */

const ProfileCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 24px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;

  @media (min-width: 769px) {
    padding: 32px;
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (min-width: 769px) {
    gap: 20px;
  }
`;

const Avatar = styled.img`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #e8f0fe;
  flex-shrink: 0;

  @media (min-width: 769px) {
    width: 84px;
    height: 84px;
    border-width: 4px;
  }
`;

const DefaultAvatar = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4285F4 0%, #1A73E8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 26px;
  flex-shrink: 0;

  @media (min-width: 769px) {
    width: 84px;
    height: 84px;
    font-size: 32px;
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 4px;
  word-break: break-word;

  @media (min-width: 769px) {
    font-size: 26px;
    margin-bottom: 6px;
  }
`;

const UserEmail = styled.div`
  font-size: 14px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
  word-break: break-all;

  svg {
    width: 15px;
    height: 15px;
    color: #94a3b8;
    flex-shrink: 0;
  }

  @media (min-width: 769px) {
    font-size: 15px;
  }
`;

const ProfileDetails = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f5;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: 769px) {
    margin-top: 20px;
    padding-top: 20px;
    gap: 12px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #64748b;
  font-size: 14px;

  svg {
    width: 18px;
    height: 18px;
    color: #4285F4;
    flex-shrink: 0;
  }

  @media (min-width: 769px) {
    font-size: 15px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

/* ── Retake quiz ─────────────────────────────────── */

const RetakeCard = styled.div`
  margin-bottom: 16px;
  position: relative;
`;

const RetakeButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 16px;
  color: #1a1a2e;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  svg {
    color: #4285F4;
  }

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const RetakeOptions = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  z-index: 10;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
`;

const RetakeOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 16px 20px;
  background: transparent;
  border: none;
  border-bottom: 1px solid #f0f0f5;
  color: #1a1a2e;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  svg {
    color: #4285F4;
  }

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fafc;
  }
`;

const RetakeOptionDesc = styled.span`
  font-size: 13px;
  color: #94a3b8;
  width: 100%;
  padding-left: 26px;
  margin-top: -4px;
`;

/* ── Menu cards ──────────────────────────────────── */

const MenuCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 16px 16px 16px 20px;
  background: transparent;
  border: none;
  border-bottom: 1px solid #f0f0f5;
  text-align: left;
  font-size: 15px;
  color: #1a1a2e;
  cursor: pointer;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  gap: 14px;

  &:last-child {
    border-bottom: none;
  }

  > svg:first-child {
    width: 22px;
    height: 22px;
    color: #4285F4;
    flex-shrink: 0;
  }

  &:hover {
    background: #f8fafc;
  }

  &.logout {
    color: #dc2626;

    > svg:first-child {
      color: #dc2626;
    }

    &:hover {
      background: #fef2f2;
    }
  }

  @media (min-width: 769px) {
    padding: 18px 20px 18px 24px;
    font-size: 16px;

    > svg:first-child {
      width: 24px;
      height: 24px;
    }
  }
`;

const MenuItemLabel = styled.div`
  flex: 1;
  min-width: 0;
`;

const MenuItemTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  font-size: 15px;
  color: #1a1a2e;

  @media (min-width: 769px) {
    font-size: 16px;
    margin-bottom: 3px;
  }
`;

const MenuItemSubtitle = styled.div`
  font-size: 13px;
  color: #94a3b8;
  line-height: 1.4;

  @media (min-width: 769px) {
    font-size: 14px;
  }
`;

const MenuChevron = styled(ChevronRightIcon)`
  width: 18px;
  height: 18px;
  color: #cbd5e1 !important;
  flex-shrink: 0;
`;

/* ── Component ───────────────────────────────────── */

function ProfilePage() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [showSwipe, setShowSwipe] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showRetakeMenu, setShowRetakeMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <Container>
        <Content>
          <LoginPrompt>
            <PromptIcon>
              <UserCircleIcon />
            </PromptIcon>
            <PromptTitle>Login to Access Your Profile</PromptTitle>
            <PromptMessage>
              Sign in to view your saved places, itineraries, and manage your account.
            </PromptMessage>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '300px', margin: '0 auto' }}>
              <LoginButton fullWidth defaultMode="login">Login</LoginButton>
              <LoginButton fullWidth defaultMode="signup">Create Account</LoginButton>
            </div>
          </LoginPrompt>
        </Content>
      </Container>
    );
  }

  // Show profile when authenticated
  return (
    <Container>
      <Content>
        <ProfileCard>
          <ProfileHeader>
            {user.picture ? (
              <Avatar src={user.picture} alt={user.name || 'User'} />
            ) : (
              <DefaultAvatar>{getInitials(user.name || user.email)}</DefaultAvatar>
            )}
            <ProfileInfo>
              <UserName>{user.name || 'Traveler'}</UserName>
              <UserEmail>
                <EnvelopeIcon />
                {user.email}
              </UserEmail>
            </ProfileInfo>
          </ProfileHeader>

          <ProfileDetails>
            <DetailItem>
              <CalendarIcon />
              <span>Member since {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
            </DetailItem>
          </ProfileDetails>
        </ProfileCard>

        <TasteProfileCard userId={user?.sub || user?.id} />

        <RetakeCard>
          <RetakeButton onClick={() => setShowRetakeMenu(prev => !prev)}>
            <ArrowPathIcon style={{ width: 18, height: 18 }} />
            Retake Travel DNA Quiz
          </RetakeButton>
          {showRetakeMenu && (
            <RetakeOptions>
              <RetakeOption onClick={() => { setShowRetakeMenu(false); setShowSwipe(true); }}>
                <SparklesIcon style={{ width: 16, height: 16 }} />
                Swipe Cards
                <RetakeOptionDesc>Quick visual style quiz</RetakeOptionDesc>
              </RetakeOption>
              <RetakeOption onClick={() => { setShowRetakeMenu(false); setShowQuiz(true); }}>
                <ArrowPathIcon style={{ width: 16, height: 16 }} />
                Quick Quiz
                <RetakeOptionDesc>5 scenario questions</RetakeOptionDesc>
              </RetakeOption>
            </RetakeOptions>
          )}
        </RetakeCard>

        <AnimatePresence>
          {showSwipe && (
            <SwipeOnboarding
              onComplete={() => { setShowSwipe(false); window.location.reload(); }}
              onClose={() => setShowSwipe(false)}
            />
          )}
          {showQuiz && (
            <TravelSoulQuiz
              onComplete={() => { setShowQuiz(false); window.location.reload(); }}
              onClose={() => setShowQuiz(false)}
            />
          )}
        </AnimatePresence>

        <MenuCard>
          <MenuItem onClick={() => navigate('/itineraries')}>
            <MapPinIcon />
            <MenuItemLabel>
              <MenuItemTitle>My Itineraries</MenuItemTitle>
              <MenuItemSubtitle>View and manage your travel plans</MenuItemSubtitle>
            </MenuItemLabel>
            <MenuChevron />
          </MenuItem>

          <MenuItem onClick={() => navigate('/saved')}>
            <BookmarkIcon />
            <MenuItemLabel>
              <MenuItemTitle>Saved Places</MenuItemTitle>
              <MenuItemSubtitle>Your favorite destinations</MenuItemSubtitle>
            </MenuItemLabel>
            <MenuChevron />
          </MenuItem>
        </MenuCard>

        <MenuCard>
          <MenuItem onClick={handleLogout} className="logout">
            <ArrowRightOnRectangleIcon />
            <MenuItemLabel>
              <MenuItemTitle>Logout</MenuItemTitle>
              <MenuItemSubtitle>Sign out of your account</MenuItemSubtitle>
            </MenuItemLabel>
          </MenuItem>
        </MenuCard>
      </Content>
    </Container>
  );
}

export default ProfilePage;
